import React, {
  memo,
  type ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDetectScroll } from '@saviynt/common';
import {
  Box,
  Button,
  FooterActionBar,
  Icon,
  InlineMessage,
  InputField,
  Loader,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import { format } from 'date-fns';

import { EndDatePicker } from '../../../../../shared/components';
import { MIN_COMMENT_LENGTH } from '../../../../../shared/constants';
import { useInvertedLocalize } from '../../../../../shared/hooks';
import { useHandleMitigatingControl } from '../../../../Details/hooks';

import './ViolationsMcEndDate.css';

interface ViolationsMcEndDateProps {
  sodKey: string;
  isOpen: boolean;
  isLoading: boolean;
  headerIcon: ReactElement;
  title: string;
  mcKeys?: number[];
  isDetailsPage?: boolean;
  handleSumbit: (
    comment: string,
    configs?: {
      endDate?: string | null;
    }
  ) => void;
  handleModalClose: () => void;
  isDisabled?: boolean;
  mitigatingControlCount?: number;
  globalDateFormat?: string;
}

/**
 * ViolationsMcEndDate
 */
function ViolationsMcEndDate({
  sodKey,
  isOpen,
  isDisabled,
  isLoading,
  headerIcon,
  title,
  mcKeys,
  isDetailsPage,
  handleSumbit,
  handleModalClose,
  mitigatingControlCount,
  globalDateFormat,
}: ViolationsMcEndDateProps) {
  const l = useInvertedLocalize();
  const sectionRef = useRef(null);
  const { isScrolled: sectionRefIsScrolled }: { isScrolled: boolean } =
    useDetectScroll(null, sectionRef);

  const [isLoadingMc, setIsLoadingMc] = useState(
    Boolean(
      (mitigatingControlCount &&
        mitigatingControlCount === 1 &&
        sodKey.length > 0) ||
        (isDetailsPage && mcKeys && mcKeys.length === 1)
    )
  );
  const [isErrorMc, setIsErrorMc] = useState(false);
  const [commentValue, setCommentValue] = useState('');
  const [isDateCritical, setIsDateCritical] = useState(false);
  const [isCommentCritical, setIsCommentCritical] = useState(false);
  const [epoch, setEpoch] = useState<{
    startEpoch: number | null;
    endEpoch: number | null;
  }>({
    startEpoch: 0,
    endEpoch: null,
  });

  const { loadMitigateRow, mitigatingControlRows } = useHandleMitigatingControl(
    { isCacheDisabled: true }
  );

  useEffect(() => {
    if (sodKey && sodKey.length > 0 && isLoadingMc) {
      const requestData = {
        id: parseInt(sodKey, 10),
        violationId: sodKey,
      };

      setIsErrorMc(false);

      loadMitigateRow(requestData)
        .catch(() => setIsErrorMc(true))
        .finally(() => setIsLoadingMc(false));
    }
  }, [loadMitigateRow, sodKey, isLoadingMc]);

  useEffect(() => {
    if (epoch.endEpoch !== null) {
      setIsDateCritical(false);
    }
  }, [epoch]);

  const getCurrentEndDate = () => {
    if (mitigatingControlRows.length === 1) {
      return mitigatingControlRows[0].endDate;
    }

    if (isDetailsPage && mcKeys && mcKeys.length === 1) {
      const targetMc = mitigatingControlRows.find(
        (row) => row.id === mcKeys[0]
      );

      return targetMc?.endDate;
    }

    return null;
  };

  // Constants
  const isSubmitButtonEnable = useMemo(
    () => commentValue.trim().length >= MIN_COMMENT_LENGTH,
    [commentValue]
  );
  const currentStartDate: string | undefined | null =
    mitigatingControlRows.length === 1
      ? mitigatingControlRows[0].startDate
      : undefined;
  const currentEndDate: string | undefined | null = getCurrentEndDate();

  const handleModalSubmit = () => {
    // Check for modes and null dates
    if (epoch.endEpoch === null) {
      setIsDateCritical(true);

      return;
    }

    const endDate = epoch.endEpoch
      ? format(epoch.endEpoch, 'yyyy-MM-dd HH:mm:ss')
      : null;

    handleSumbit(commentValue, {
      endDate,
      ...(isDetailsPage && mcKeys && mcKeys.length === 1 ? { mcKeys } : {}),
    });
  };

  const handleCloseModal = () => {
    setIsLoadingMc(false);
    handleModalClose();
  };

  const renderBody = () => {
    if (isLoadingMc) {
      return (
        <Box tag='div' className='Sod-MitigateControlsEndDate-loader'>
          <Loader kind='dots' size='small' format='inline' color='brand' />
        </Box>
      );
    }

    if (isErrorMc) {
      return (
        <Box tag='div' className='Sod-MitigateControlsEndDate-errorWrapper'>
          <Typography kind='body2'>
            {l('Error loading summary details, please try again.')}
          </Typography>
          <Button
            type='button'
            kind='outlined'
            isEnabled
            onClick={() => {
              setIsLoadingMc(true);
            }}>
            {l('Retry')}
          </Button>
        </Box>
      );
    }

    return (
      <>
        <EndDatePicker
          isCritical={isDateCritical}
          setIsCritical={setIsDateCritical}
          startDate={currentStartDate}
          currentEndDate={currentEndDate}
          globalDateFormat={globalDateFormat ?? ''}
          setEpoch={setEpoch}
        />
        <Box className='Sod-MitigateControlsEndDate-commentWrapper'>
          <InputField
            name='mitigate-controls-end-date-action-comment'
            kind='multiline'
            label={
              <Typography
                kind='label'
                htmlFor='mitigate-controls-end-date-action-commen'>
                {l('Comments')}
              </Typography>
            }
            tabIndex={0}
            value={commentValue}
            setValue={setCommentValue}
            placeholder={l('Provide a reason for your decision')}
            isRequired={!isSubmitButtonEnable}
            isCritical={isCommentCritical}
            setIsCritical={setIsCommentCritical}
            CriticalHelperText={
              <InlineMessage
                text={
                  commentValue.trim().length < 1
                    ? l('This required field is incomplete')
                    : `Your comments must contain more than ${
                        MIN_COMMENT_LENGTH - 1
                      } characters.`
                }
                colorTheme='critical'
                size='small'
                leftIcon={<Icon kind='AlertCriticalFilled' />}
              />
            }
          />
        </Box>
      </>
    );
  };

  return (
    <ModalIsland
      isOpen={isOpen}
      onClose={handleCloseModal}
      headerIcon={headerIcon}
      title={title}
      isCloseButtonVisible={!isDisabled}
      contentBody={
        <Box className='Sod-MitigateControlsEndDate-bodyWrapper'>
          {renderBody()}
        </Box>
      }
      FooterActionBarComp={
        <FooterActionBar size='large' isScrolled={sectionRefIsScrolled}>
          <div />
          <div className='Sod-MitigateControlsEndDate-footerActionButtons'>
            <Button
              type='button'
              kind='ghost'
              size='medium'
              isDisabled={Boolean(isDisabled ?? isLoading) || isLoadingMc}
              onClick={handleCloseModal}>
              {l('Cancel')}
            </Button>
            <Button
              type='button'
              kind={isSubmitButtonEnable ? 'filled' : 'outlined'}
              size='medium'
              onClick={() => {
                if (isSubmitButtonEnable) {
                  handleModalSubmit();

                  return;
                }

                setIsCommentCritical(true);
              }}
              isDisabled={isDisabled && !isLoading && !isLoadingMc}
              isLoading={isLoading}
              loaderText={l('Saving')}>
              {l('Save')}
            </Button>
          </div>
        </FooterActionBar>
      }
      className='Sod-MitigateControlsEndDate'
    />
  );
}

ViolationsMcEndDate.defaultProps = {
  isDisabled: false,
  mcKeys: undefined,
  isDetailsPage: false,
  mitigatingControlCount: undefined,
  globalDateFormat: 'MMM dd, yyyy',
};

export default memo(ViolationsMcEndDate);
